import { useState, useEffect } from 'react';
import Layout from '@/components/feature/Layout';
import { supabase, Venta, Producto, Categoria, DetalleVenta } from '@/lib/supabase';

export default function ReportesPage() {
  const [ventas, setVentas] = useState<Venta[]>([]);
  const [productos, setProductos] = useState<Producto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [detalles, setDetalles] = useState<DetalleVenta[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ventasRes, prodRes, catRes, detRes] = await Promise.all([
          supabase.from('ventas').select('*').order('fecha', { ascending: false }),
          supabase.from('productos').select('*'),
          supabase.from('categorias').select('*'),
          supabase.from('detalle_ventas').select('*'),
        ]);
        if (ventasRes.data) setVentas(ventasRes.data);
        if (prodRes.data) setProductos(prodRes.data);
        if (catRes.data) setCategorias(catRes.data);
        if (detRes.data) setDetalles(detRes.data);
      } catch {
        // error
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const totalVentas = ventas.reduce((s, v) => s + Number(v.total), 0);
  const totalProductos = productos.length;
  const totalCategorias = categorias.length;
  const agotados = productos.filter((p) => p.stock === 0).length;
  const stockBajo = productos.filter((p) => p.stock > 0 && p.stock <= p.stock_minimo).length;
  const inventarioValorizado = productos.reduce((s, p) => s + Number(p.precio_venta) * p.stock, 0);

  const metodosPago = {
    efectivo: ventas.filter((v) => v.metodo_pago === 'efectivo').length,
    qr: ventas.filter((v) => v.metodo_pago === 'qr').length,
  };

  const topMasVendidos = (() => {
    const ventasPorProducto: Record<string, { cantidad: number; totalBs: number }> = {};
    detalles.forEach((d) => {
      if (!ventasPorProducto[d.producto_id]) {
        ventasPorProducto[d.producto_id] = { cantidad: 0, totalBs: 0 };
      }
      ventasPorProducto[d.producto_id].cantidad += d.cantidad;
      ventasPorProducto[d.producto_id].totalBs += Number(d.subtotal);
    });
    return Object.entries(ventasPorProducto)
      .map(([productoId, data]) => {
        const prod = productos.find((p) => p.id === productoId);
        return {
          id: productoId,
          nombre: prod?.nombre || 'Producto eliminado',
          cantidad: data.cantidad,
          totalBs: data.totalBs,
        };
      })
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 5);
  })();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="spinner w-10 h-10 border-3 border-[#2563EB] border-t-transparent mx-auto mb-4"></div>
            <p className="text-sm text-gray-500">Cargando reportes...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in space-y-6">
        <div>
          <h2 className="text-xl font-bold text-[#111827]">Reportes</h2>
          <p className="text-sm text-gray-500 mt-0.5">Análisis y estadísticas del negocio</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#2563EB]/10 text-[#2563EB] mb-2">
              <i className="ri-shopping-cart-2-line w-5 h-5 flex items-center justify-center"></i>
            </div>
            <p className="text-2xl font-bold text-[#111827]">{ventas.length}</p>
            <p className="text-xs text-gray-500">Ventas Totales</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#16A34A]/10 text-[#16A34A] mb-2">
              <i className="ri-money-dollar-circle-line w-5 h-5 flex items-center justify-center"></i>
            </div>
            <p className="text-2xl font-bold text-[#111827]">Bs. {totalVentas.toLocaleString('es-BO')}</p>
            <p className="text-xs text-gray-500">Ingresos Totales</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100 text-purple-600 mb-2">
              <i className="ri-stack-line w-5 h-5 flex items-center justify-center"></i>
            </div>
            <p className="text-2xl font-bold text-[#111827]">Bs. {inventarioValorizado.toLocaleString('es-BO')}</p>
            <p className="text-xs text-gray-500">Inventario Valorizado</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#DC2626]/10 text-[#DC2626] mb-2">
              <i className="ri-close-circle-line w-5 h-5 flex items-center justify-center"></i>
            </div>
            <p className="text-2xl font-bold text-[#111827]">{agotados}</p>
            <p className="text-xs text-gray-500">Productos Agotados</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#111827] mb-4">Resumen de Inventario</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Total Productos</span>
                <span className="text-sm font-semibold text-[#111827]">{totalProductos}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Categorías</span>
                <span className="text-sm font-semibold text-[#111827]">{totalCategorias}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Productos con Stock Bajo</span>
                <span className="text-sm font-semibold text-[#F59E0B]">{stockBajo}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <span className="text-sm text-gray-500">Productos Agotados</span>
                <span className="text-sm font-semibold text-[#DC2626]">{agotados}</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-gray-500">Valor Total Inventario</span>
                <span className="text-sm font-semibold text-[#111827]">Bs. {inventarioValorizado.toLocaleString('es-BO')}</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#111827] mb-4">Métodos de Pago</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#16A34A]"></div>
                  <span className="text-sm text-gray-600">Efectivo</span>
                </div>
                <span className="text-sm font-semibold text-[#111827]">{metodosPago.efectivo}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-50">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#2563EB]"></div>
                  <span className="text-sm text-gray-600">QR</span>
                </div>
                <span className="text-sm font-semibold text-[#111827]">{metodosPago.qr}</span>
              </div>

            </div>

            {productos.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2">Top 5 - Mayor Stock</p>
                {[...productos]
                  .sort((a, b) => b.stock - a.stock)
                  .slice(0, 5)
                  .map((p, idx) => (
                    <div key={p.id} className="flex items-center justify-between py-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs text-gray-400 w-4">{idx + 1}</span>
                        <span className="text-xs text-gray-700 truncate">{p.nombre}</span>
                      </div>
                      <span className="text-xs font-medium text-[#111827] whitespace-nowrap ml-2">{p.stock} uds</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {topMasVendidos.length > 0 && (
          <div className="bg-white rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[#111827] mb-1">Top 5 - Productos Más Vendidos</h3>
            <p className="text-xs text-gray-400 mb-4">Ranking por unidades vendidas</p>
            <div className="space-y-1">
              {topMasVendidos.map((item, idx) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        idx === 0
                          ? 'bg-[#F59E0B] text-white'
                          : idx === 1
                          ? 'bg-gray-300 text-gray-600'
                          : idx === 2
                          ? 'bg-[#D97706]/30 text-[#D97706]'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#111827] truncate">{item.nombre}</p>
                      <p className="text-xs text-gray-400">Bs. {item.totalBs.toLocaleString('es-BO')}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <p className="text-sm font-bold text-[#111827]">{item.cantidad}</p>
                    <p className="text-xs text-gray-400">vendidos</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}